import type { User } from '@auth0/auth0-react'

export type ProfileFieldOption = { value: string; label: string }

export type ProfileField = {
  key: string
  label: string
  value: string
  editable?: boolean
  placeholder?: string
  type?: 'text' | 'boolean' | 'select'
  options?: ProfileFieldOption[]
}

// Mantém em sync manualmente com apps/auth-screens/src/screens/signup/index.tsx
// (options do <select> de gênero no cadastro) — não há pacote compartilhado
// entre auth-screens e default-spa.
const GENDER_OPTIONS: ProfileFieldOption[] = [
  { value: 'female', label: 'Feminino' },
  { value: 'male', label: 'Masculino' },
  { value: 'non_binary', label: 'Não binário' },
  { value: 'prefer_not_to_say', label: 'Prefiro não informar' },
]

export type Auth0Identity = {
  provider?: string
  connection?: string
  user_id?: string
  isSocial?: boolean
  profileData?: { email?: string; name?: string }
}

export type MgmtUserProfile = {
  user_id?: string
  email?: string
  email_verified?: boolean
  username?: string
  name?: string
  nickname?: string
  phone_number?: string
  updated_at?: string
  user_metadata?: Record<string, unknown>
  app_metadata?: Record<string, unknown>
  identities?: Auth0Identity[]
  [key: string]: unknown
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {}
}

function metaFrom(
  sources: Array<Record<string, unknown> | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const s of sources) {
    if (s) Object.assign(out, s)
  }
  return out
}

function pick(...vals: unknown[]) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v)
    }
  }
  return ''
}

/**
 * Monta campos do Personal Info (prints Alelo My Account).
 * Aceita User do SDK e/ou perfil Management API (via BFF).
 */
export function buildProfileFields(
  user: User | undefined,
  mgmt?: MgmtUserProfile | null,
): ProfileField[] {
  const sdk = (user || {}) as Record<string, unknown>
  // Precedência: metadados do perfil (Management API via BFF) > claims da
  // sessão. A sessão pode estar antiga depois de um PATCH.
  const um = metaFrom([
    asRecord(sdk['https://alelo.com.br/user_metadata']),
    asRecord(sdk['https://acesso-b2c-dev.siteteste.inf.br/user_metadata']),
    asRecord(sdk.user_metadata),
    asRecord(mgmt?.user_metadata),
  ])
  const am = metaFrom([
    asRecord(sdk['https://alelo.com.br/app_metadata']),
    asRecord(sdk['https://acesso-b2c-dev.siteteste.inf.br/app_metadata']),
    asRecord(sdk.app_metadata),
    asRecord(mgmt?.app_metadata),
  ])

  const displayName = pick(
    um.fullName,
    um.full_name,
    um.name,
    mgmt?.name && mgmt.name !== mgmt.email ? mgmt.name : undefined,
    user?.name && user.name !== user.email ? user.name : undefined,
    user?.nickname,
  )

  const rows: ProfileField[] = [
    {
      key: 'name',
      label: 'Nome completo',
      value: displayName,
      editable: true,
    },
    {
      key: 'email',
      label: 'E-mail',
      value: pick(mgmt?.email, user?.email),
    },
    {
      key: 'mother_name',
      label: 'Nome da mãe',
      value: pick(um.motherName, um.nome_mae),
      editable: true,
      placeholder: 'Nome da mãe',
    },
    {
      key: 'last_email_update',
      label: 'Data da última alteração de e-mail',
      value: pick(um.last_email_update, um.lastEmailUpdateDate),
      editable: false,
    },
    {
      key: 'anti_fraud_code',
      label: 'Código antifraude',
      value: pick(um.anti_fraud_code, um.antiFraudCode, am.anti_fraud_code),
      editable: true,
      placeholder: 'Código antifraude',
    },
    {
      key: 'last_phone_update',
      label: 'Data da última alteração de telefone',
      value: pick(um.last_phone_update, um.lastPhoneUpdateDate),
      editable: false,
    },
    {
      key: 'force_mfa',
      label: 'Confirmação em duas etapas obrigatória',
      value: pick(am.force_mfa, um.force_mfa),
      editable: false,
      type: 'boolean',
    },
    {
      key: 'migrar',
      label: 'Conta migrada',
      value: pick(am.migrated, am.migrar, um.migrar, am.migrated_from),
      editable: false,
      type: 'boolean',
    },
    {
      key: 'document_number',
      label: 'CPF',
      value: pick(
        um.documentNumber,
        um.cpf,
        sdk['https://alelo.com.br/cpf'],
      ),
      editable: true,
      placeholder: '000.000.000-00',
    },
    {
      key: 'external_user_id',
      label: 'Identificador externo',
      value: pick(am.external_user_id, am.externalUserId, um.external_user_id),
    },
    {
      key: 'user_id',
      label: 'Identificador da conta',
      value: pick(mgmt?.user_id, user?.sub),
    },
    {
      key: 'username',
      label: 'Nome de usuário',
      value: pick(
        mgmt?.username,
        (user as { username?: string })?.username,
        um.username,
        um.documentNumber,
        um.cpf,
      ),
    },
    {
      key: 'prova_vida',
      label: 'Data da prova de vida',
      value: pick(
        um.prova_vida,
        um.data_prova_vida,
        um.proof_of_life_date,
        um.liveness_date,
      ),
      editable: true,
    },
    {
      key: 'gender',
      label: 'Gênero',
      value: pick(um.gender, um.genero, um.sexo),
      editable: true,
      placeholder: 'Selecione',
      type: 'select',
      options: GENDER_OPTIONS,
    },
    {
      key: 'phone',
      label: 'Celular',
      value: pick(mgmt?.phone_number, um.phoneNumber, um.phone),
      editable: true,
      placeholder: '(11) 91234-5678',
    },
    {
      key: 'email_verified',
      label: 'E-mail confirmado',
      value:
        mgmt?.email_verified != null
          ? String(mgmt.email_verified)
          : user?.email_verified == null
            ? ''
            : String(user.email_verified),
    },
    {
      key: 'org_id',
      label: 'Organização',
      value: pick((user as { org_id?: string })?.org_id),
    },
    {
      key: 'updated_at',
      label: 'Última atualização',
      value: pick(mgmt?.updated_at, user?.updated_at),
    },
  ]

  // Mantém todos os campos (placeholders quando vazios) — fiel aos prints
  return rows
}

export function profileCompletion(fields: ProfileField[]) {
  const mandatoryKeys = ['email', 'username', 'user_id', 'name']
  const mandatory = mandatoryKeys.filter((k) =>
    fields.some((f) => f.key === k && f.value),
  )
  const optionalKeys = fields
    .map((f) => f.key)
    .filter((k) => !mandatoryKeys.includes(k))
  const optionalDone = optionalKeys.filter((k) =>
    fields.some((f) => f.key === k && f.value),
  ).length
  const optionalTotal = Math.max(optionalKeys.length, 22)
  const pct = Math.round(
    ((mandatory.length / mandatoryKeys.length) * 0.55 +
      (optionalDone / optionalTotal) * 0.45) *
      100,
  )
  return {
    percent: Math.min(100, Math.max(0, pct)),
    mandatoryDone: mandatory.length,
    mandatoryTotal: mandatoryKeys.length,
    optionalDone,
    optionalTotal,
  }
}

export function initials(name?: string | null) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function socialIdentities(identities?: Auth0Identity[]) {
  if (!identities?.length) return []
  return identities.filter(
    (i) =>
      i.isSocial ||
      (i.provider &&
        !['auth0', 'email', 'sms', 'Username-Password-Authentication'].includes(
          i.provider,
        ) &&
        i.provider !== 'Username-Password-Authentication'),
  )
}

export function dbIdentities(identities?: Auth0Identity[]) {
  if (!identities?.length) return []
  return identities.filter(
    (i) =>
      !i.isSocial &&
      (i.provider === 'auth0' ||
        i.connection === 'Username-Password-Authentication' ||
        !i.provider),
  )
}
